import { Link, useParams } from "wouter";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  ArrowLeft,
  Edit,
  Star,
  Coins,
  TicketCheck,
  Users,
  Ban,
} from "lucide-react";
import { usePerk, useUpdatePerkStatus, usePerkRedemptions } from "@/hooks/usePerks";
import { PromoCodeManager } from "./components/PromoCodeManager";
import { OptOutsTab } from "./components/OptOutsTab";
import { format } from "date-fns";
import type { PerkStatus } from "@/types/generated/cms.generated";

const statusVariant: Record<PerkStatus, "default" | "secondary" | "outline" | "destructive"> = {
  Active: "default",
  Inactive: "outline",
  Expired: "destructive",
};

export default function PerkDetail() {
  const params = useParams<{ perkId: string }>();
  const perkId = params.perkId;
  const { data: perk, isLoading, error } = usePerk(perkId);
  const updateStatus = useUpdatePerkStatus();
  const { data: redemptions = [], isLoading: redemptionsLoading } = usePerkRedemptions(perkId);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Perk Detail" />
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading perk</AlertTitle>
            <AlertDescription>Failed to load perk details. Please try refreshing.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (isLoading || !perk) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Perk Detail" />
        <div className="flex-1 p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const isActive = perk.status === "Active";

  const handleToggleActive = (checked: boolean) => {
    if (perk.id) {
      updateStatus.mutate({ id: perk.id, status: checked ? "Active" : "Inactive" });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Header title={perk.title || "Perk Detail"} description={perk.vendorName || undefined}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={handleToggleActive}
              disabled={updateStatus.isPending || perk.status === "Expired"}
            />
            <Label className="text-sm font-medium">
              {perk.status === "Expired" ? "Expired" : isActive ? "Active" : "Inactive"}
            </Label>
          </div>
          <Link href={`/perks/${perkId}/edit`}>
            <Button variant="outline">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Link href="/perks">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </Link>
        </div>
      </Header>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Badge variant={statusVariant[perk.status || "Inactive"]}>
                  {perk.status}
                </Badge>
              </div>
              <p className="text-lg font-semibold mt-1">Status</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Coins className="w-4 h-4" />
                <span>Coin Cost</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perk.coinCost || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <TicketCheck className="w-4 h-4" />
                <span>Total Redemptions</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perk.totalRedemptions || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Ban className="w-4 h-4" />
                <span>Opt-Outs</span>
              </div>
              <p className="text-2xl font-bold mt-1">{perk.optOutCount || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Detail info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Details
              {perk.isFeatured && (
                <Badge variant="outline">
                  <Star className="w-3 h-3 mr-1 text-yellow-500 fill-yellow-500" />
                  Featured
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {perk.imageUrl && (
              <div className="mb-4">
                <img
                  src={perk.imageUrl}
                  alt={perk.title || "Perk"}
                  className="rounded-lg max-h-48 object-cover"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Description</p>
                <p>{perk.description || "No description"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Delivery Method</p>
                <Badge variant="outline">{perk.deliveryMethod}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Value Type</p>
                <p>{perk.valueType}{perk.discountValue ? `: ${perk.discountValue}${perk.valueType === "Percentage" ? "%" : ""}` : ""}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Visibility</p>
                <Badge variant={perk.isVisible ? "default" : "secondary"}>
                  {perk.isVisible ? "Visible" : "Hidden"}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Start Date</p>
                <p>{perk.startDate ? format(new Date(perk.startDate), "MMM d, yyyy") : "-"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">End Date</p>
                <p>{perk.endDate ? format(new Date(perk.endDate), "MMM d, yyyy") : "-"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="promo-codes">
          <TabsList>
            <TabsTrigger value="promo-codes" className="flex items-center gap-1">
              <TicketCheck className="w-4 h-4" />
              Promo Codes
            </TabsTrigger>
            <TabsTrigger value="redemptions" className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              Redemptions ({perk.totalRedemptions || 0})
            </TabsTrigger>
            <TabsTrigger value="opt-outs" className="flex items-center gap-1">
              <Ban className="w-4 h-4" />
              Opt-Outs ({perk.optOutCount || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="promo-codes" className="mt-4">
            {perkId && <PromoCodeManager perkId={perkId} />}
          </TabsContent>

          <TabsContent value="redemptions" className="mt-4">
            <Card>
              <CardContent className="p-0">
                {redemptionsLoading ? (
                  <div className="p-4 space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : redemptions.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No redemptions yet.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Coins Spent</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {redemptions.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.employeeName}</TableCell>
                          <TableCell>{r.organizationName}</TableCell>
                          <TableCell className="font-mono text-xs">{r.redemptionReference}</TableCell>
                          <TableCell>{r.coinsSpent}</TableCell>
                          <TableCell>
                            {r.createdAt ? format(new Date(r.createdAt), "MMM d, yyyy HH:mm") : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="opt-outs" className="mt-4">
            {perkId && <OptOutsTab perkId={perkId} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
